import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { ROLE_LABELS, type Permission } from 'shared';
import { useAuth } from '../context/AuthContext';
import { BrandMark, Icon, type IconName } from './icons';

/** An entry with no `permission` is an own-account action every signed-in user has. */
interface NavItem { to: string; label: string; icon: IconName; permission?: Permission }

/** The menu is filtered by the same permissions the server enforces — one source. */
const NAV: { label: string; items: NavItem[] }[] = [
  {
    label: 'Operasional',
    items: [
      { to: '/orders', label: 'Pesanan', icon: 'order', permission: 'order:read' },
      { to: '/stock', label: 'Stok Darah', icon: 'droplet', permission: 'stock:read' },
      { to: '/donations', label: 'Donasi', icon: 'heart', permission: 'donation:read' },
      { to: '/donors', label: 'Pendonor', icon: 'users', permission: 'donor:read' },
      { to: '/events', label: 'Kegiatan Donor', icon: 'calendar', permission: 'event:read' },
      { to: '/forms', label: 'Form Asesmen', icon: 'clipboard', permission: 'form:read' },
    ],
  },
  {
    label: 'Layanan',
    items: [
      { to: '/service-requests', label: 'Permintaan', icon: 'clipboard', permission: 'service_request:read' },
      { to: '/service-types', label: 'Jenis Layanan', icon: 'tag', permission: 'service:read' },
      { to: '/service-forms', label: 'Form Layanan', icon: 'clipboard', permission: 'service_form:read' },
      { to: '/service-dashboard', label: 'Dasbor Layanan', icon: 'chart', permission: 'service_request:read' },
    ],
  },
  {
    label: 'Keuangan',
    items: [
      { to: '/payments', label: 'Pembayaran', icon: 'payment', permission: 'payment:read' },
      { to: '/ledger', label: 'Buku Besar', icon: 'ledger', permission: 'ledger:read' },
    ],
  },
  {
    label: 'Jaringan',
    items: [
      { to: '/branches', label: 'Cabang UDD', icon: 'branch', permission: 'branch:read' },
      { to: '/hospitals', label: 'Rumah Sakit', icon: 'hospital', permission: 'hospital:read' },
    ],
  },
  {
    label: 'Referensi',
    items: [
      { to: '/blood-reference', label: 'Acuan Darah', icon: 'flask', permission: 'blood:read' },
      { to: '/pricing', label: 'Tarif BPPD', icon: 'tag', permission: 'price:read' },
    ],
  },
  {
    label: 'Sistem',
    items: [
      { to: '/users', label: 'Pengguna', icon: 'idCard', permission: 'user:read' },
      { to: '/settings', label: 'Kredensial', icon: 'key', permission: 'config:read' },
    ],
  },
  {
    label: 'Akun',
    items: [
      { to: '/change-password', label: 'Ganti Password', icon: 'lock' },
    ],
  },
];

const COLLAPSED_KEY = 'pmi.sidebar.collapsed';

const initials = (name: string) => name
  .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '·';

export function Layout() {
  const { user, logout, can } = useAuth();
  const { pathname } = useLocation();

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === '1');
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Navigating on a phone should hand the screen back to the page.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle('drawer-open', drawerOpen);
    if (!drawerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  // A group whose every entry is out of reach should not leave a stray heading.
  const groups = NAV
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.permission || can(i.permission)) }))
    .filter((g) => g.items.length > 0);

  const brand = (
    <span className="brand">
      <BrandMark />
      <span className="brand-text">
        <strong>Monitoring Darah</strong>
        <span>Palang Merah Indonesia</span>
      </span>
    </span>
  );

  return (
    <div className={`app${collapsed ? ' is-collapsed' : ''}`}>
      <header className="topbar">
        <button
          className="icon-btn"
          onClick={() => setDrawerOpen(true)}
          aria-label="Buka menu"
          aria-expanded={drawerOpen}
        >
          <Icon name="menu" />
        </button>
        {brand}
      </header>

      {drawerOpen && <div className="scrim" onClick={() => setDrawerOpen(false)} />}

      <aside className={`sidebar${drawerOpen ? ' is-open' : ''}`}>
        <div className="sidebar-head">
          {brand}
          <button
            className="icon-btn sidebar-collapse"
            onClick={() => setCollapsed(true)}
            aria-label="Ciutkan navigasi"
            title="Ciutkan navigasi"
          >
            <Icon name="collapse" />
          </button>
          <button
            className="icon-btn sidebar-expand"
            onClick={() => setCollapsed(false)}
            aria-label="Bentangkan navigasi"
            title="Bentangkan navigasi"
          >
            <Icon name="expand" />
          </button>
          <button
            className="icon-btn sidebar-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Tutup menu"
          >
            <Icon name="close" />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Navigasi utama">
          {groups.map((group) => (
            <div className="nav-group" key={group.label}>
              <span className="nav-group-label">{group.label}</span>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon name={item.icon} />
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="user-chip" title={user?.name}>
            <span className="avatar">{initials(user?.name ?? '')}</span>
            <span className="who">
              <b>{user?.name}</b>
              <span>{user ? ROLE_LABELS[user.role] : ''}</span>
            </span>
          </div>
          <button
            className="icon-btn"
            onClick={logout}
            aria-label="Keluar"
            title="Keluar"
          >
            <Icon name="logout" />
          </button>
        </div>
      </aside>

      <main className="main">
        <div className="content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
